import {Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChange, SimpleChanges} from '@angular/core';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {CssVariablesService} from '@shared/services/util/css-variables.service';

interface DimensionWithoutUnits {
  height: number;
  width: number;
}

interface DimensionWithUnits {
  height: string;
  width: string;
}

interface StyleProperties {
  display?: string;
  fill?: string
}
export enum APP_ICON_ROTATE {
  none = 0,
  ninety = 90,
  oneEighty = 180,
  twoSeventy = 270,
}


type IconStyle = DimensionWithUnits & StyleProperties;

export type AppIconSize = '2xs' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl';

type DisplayType = 'block' | 'flex' | 'inline';

@Component({
  selector: 'app-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  standalone: false
})
export class IconComponent implements OnInit, OnChanges {

  private readonly logger: AceLogger;
  private readonly DEGREE_SUFFIX = 'deg';
  private readonly DEFAULT_SIZE: AppIconSize = 'lg';
  private readonly SPIN_ICON_NAME: string = 'spinner';
  private readonly ICON_PROPERTIES = {path: 'assets/images/icons/', extension: '.svg'};
  private readonly sizes: Map<AppIconSize, DimensionWithoutUnits> = new Map([
    ['2xs', {height: 10, width: 10}],
    ['xs', {height: 12, width: 12}],
    ['sm', {height: 14, width: 14}],
    ['lg', {height: 20, width: 20}],
    ['xl', {height: 24, width: 24}],
    ['2xl', {height: 32, width: 32}],
  ]);

  private readonly CSS_PROPERTIES = {
    fillColor: '--app-icon-fill-color',
    hoverColor: '--app-icon-hover-color',
    rotate: '--app-icon-rotate',
    rotateTransition: '--app-icon-rotate-transition'
  };

  @Input() name: string;
  @Input() cssClasses: string;
  @Input() fillColor: string;
  @Input() hoverColor: string;
  @Input() rotate: APP_ICON_ROTATE;
  @Input() rotateTransitionShorthand: string;
  @Input() display: DisplayType;
  @Input() size: AppIconSize = this.DEFAULT_SIZE;
  @Input() spinner:  boolean | '';

  readonly SPIN_ANIMATION_CLASS_NAME = 'spin';

  showSpinner: boolean;
  iconStyle: IconStyle;
  iconPath: string;
  spinnerPath: string;

  constructor(private cssVariablesService: CssVariablesService,
              private ref: ElementRef,
              logService: AceLoggerService) {
    this.logger = logService.create(IconComponent.name);
  }

  /**
   * Test whether the spinner should be controlled by click
   */
  private respondToClickOnly(value: typeof this.spinner): boolean {
    // Check for null and NOT undefined (hence triple equals and not double), as if the 'spinner' attribute is not
    // defined at all, the spinner should not be shown. However, if the 'spinner' attribute is used with empty or null
    // value, e.g. spinner or spinner="" or spinner="null", then the spinner should be shown on click
    return value === null || value === '';
  }

  @HostListener('click')
  private triggerLoading() {
    // Only respond to a click when no variable is passed in, otherwise the spinner is controlled by the variable, not a click
    if (this.respondToClickOnly(this.spinner)) {
      this.showSpinner = true;
    }
  }

  ngOnInit(): void {
    this.spinnerPath = this.getIconPath(this.SPIN_ICON_NAME);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const spinnerChange: SimpleChange = changes.spinner;
    if (spinnerChange && !this.respondToClickOnly(spinnerChange.currentValue)) {
      this.showSpinner = !!spinnerChange.currentValue;
    }

    /* CSS properties values set in the via @Input() var changes override those defined by a parent's CSS properties */
    if (changes.fillColor) {
      this.cssVariablesService.setProperty(this.ref.nativeElement, this.CSS_PROPERTIES.fillColor, this.fillColor);
    }
    if (changes.hoverColor) {
      this.cssVariablesService.setProperty(this.ref.nativeElement, this.CSS_PROPERTIES.hoverColor, this.hoverColor);
    }
    if (changes.rotate) {
      this.cssVariablesService.setProperty(this.ref.nativeElement, this.CSS_PROPERTIES.rotate, this.rotate ? this.addDegreeSuffix(this.rotate) : null);
    }
    if (changes.rotateTransitionShorthand) {
      this.cssVariablesService.setProperty(this.ref.nativeElement, this.CSS_PROPERTIES.rotateTransition, this.rotateTransitionShorthand);
    }
    if (changes.size || changes.display) {
      if (changes.size) {
        if (!this.sizes.has(this.size)) {
          if (this.size != null) {
            this.logger.warn(`No dimensions found for size '${this.size}'. Defaulting to '${this.DEFAULT_SIZE}': ${JSON.stringify(this.sizes.get(this.DEFAULT_SIZE))}`);
          }
          this.size = this.DEFAULT_SIZE;
        }
      }
      this.iconStyle = this.generateStyle();
    }
    if (changes.name) {
      /* If the file is not found, a 404 error will be thrown */
      this.iconPath = this.getIconPath(this.name);
    }
  }

  private getIconPath(iconName: string): string {
    return this.ICON_PROPERTIES.path + iconName + this.ICON_PROPERTIES.extension;
  }

  private generateStyle(): IconStyle {
    const dimensionWithoutUnits: DimensionWithoutUnits = this.sizes.get(this.size);
    const dimensionWithUnits: DimensionWithUnits = {width: dimensionWithoutUnits.width + 'px', height: dimensionWithoutUnits.height + 'px'};
    return {...dimensionWithUnits, ...this.display != null && {display: this.display}};
  }

  private addDegreeSuffix(degrees: APP_ICON_ROTATE): string {
    return degrees + this.DEGREE_SUFFIX;
  }

}
